import { redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { readAuthToken } from '@noor/api';
import { env, FIRST_PAGE } from '@noor/config';
import {
  moderationQueries,
  moderationSearchToFilter,
  type ModerationEntityType,
  type ModerationListSearch,
} from '@/entities/moderation';

/**
 * Shared wiring for the four moderation queue routes (file-based routing
 * needs one file per path, but the guard, prefetch, and param patching are
 * identical). The URL stays the single source of truth for
 * status/search/date/page.
 */

/** Auth guard (mirrors the other protected routes; MSW bypasses it in dev). */
export function requireAuthBeforeLoad(): void {
  if (!env.VITE_ENABLE_MSW && !readAuthToken()) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/login' });
  }
}

export function moderationLoaderDeps({ search }: { search: ModerationListSearch }) {
  return search;
}

/** Best-effort, non-blocking queue prefetch for instant tab switches. */
export function moderationRouteLoader(entityType: ModerationEntityType) {
  return ({
    context,
    deps,
  }: {
    context: { queryClient: QueryClient };
    deps: ModerationListSearch;
  }) => {
    void context.queryClient
      .prefetchQuery(moderationQueries.list(entityType, moderationSearchToFilter(deps)))
      .catch(() => undefined);
  };
}

export type SearchNavigator = (options: {
  search: (prev: ModerationListSearch) => ModerationListSearch;
}) => void;

/**
 * Patch URL search params. Any filter change resets to the first page;
 * explicit page jumps keep their target.
 */
export function createModerationParamsChange(
  search: ModerationListSearch,
  navigate: SearchNavigator,
): (next: Partial<ModerationListSearch>) => void {
  return (next) => {
    const resetPage = !('page' in next) && search.page !== FIRST_PAGE;
    navigate({
      search: (prev) => ({
        ...prev,
        ...next,
        ...(resetPage ? { page: FIRST_PAGE } : {}),
      }),
    });
  };
}
